import { useEffect, useState, useRef } from "react";
import Navbar from "./components/Navbar/Navbar.jsx";
import About from "./components/About/About.jsx";
import Skills from "./components/Skills/Skills.jsx";
import Experience from "./components/Experience/Experience.jsx";
import CertificationSection from "./components/Certification/CertificationSection.jsx";
import Education from "./components/Education/Education.jsx";
import Projects from "./components/Work/Work.jsx";
import Contact from "./components/Contact/Contact.jsx";
import Footer from "./components/Footer/Footer.jsx";
import BlurBlob from './components/BlurBlob.jsx';

const App = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const timerRef = useRef(null); // Ref to store the timer ID

  // Helper function to detect if the user is on an iOS device
  const isIOS = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
      console.log('beforeinstallprompt event fired. Install button is now visible.');

      // Set a timer to hide the prompt after 8 seconds
      timerRef.current = setTimeout(() => {
        setShowInstallButton(false);
        console.log('PWA install prompt auto-hidden after 8 seconds.');
      }, 8000); // 8 seconds
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show the instructions immediately and set a timer
    if (isIOS() && !window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallButton(true); // Re-using this state for iOS instructions visibility
      timerRef.current = setTimeout(() => {
        setShowInstallButton(false);
        console.log('iOS install instructions auto-hidden after 8 seconds.');
      }, 8000); // 8 seconds
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      // Clear the timer if the component unmounts or effect re-runs
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      setShowInstallButton(false); // Hide the button immediately on click
      if (timerRef.current) {
        clearTimeout(timerRef.current); // Clear the auto-hide timer
      }
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the A2HS prompt: ${outcome}`);
      setDeferredPrompt(null);
    }
  };

  // Function to dismiss the alert manually
  const handleDismissAlert = () => {
    setShowInstallButton(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current); // Clear the auto-hide timer
    }
    console.log('PWA install prompt manually dismissed.');
  };

  return (
    <div className="bg-[#050414] relative min-h-screen">

      {/* Background blur blob */}
      <BlurBlob position={{ top: '35%', left: '20%' }} size={{ width: '30%', height: '40%' }} />

      {/* Background grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
      
      <div className="relative pt-20">
        <Navbar />
        <About />
        <Skills />
        <Experience />
        <Projects /> {/* Projects component added here as it was in your first App.jsx */}
        <Education />
        <CertificationSection/>
        <Contact />
        <Footer />
      </div>

      {/* Conditional PWA Install Button for Android/Desktop */}
      {showInstallButton && !isIOS() && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 p-4 bg-gray-800 text-white rounded-xl shadow-2xl z-50 flex flex-col items-center justify-center space-y-2 max-w-sm w-11/12 md:w-auto animate-fade-in-up">
          <button
            onClick={handleDismissAlert}
            className="absolute top-2 right-2 text-gray-400 hover:text-white text-lg font-bold"
            aria-label="Dismiss"
          >
            &times;
          </button>
          <p className="text-sm text-center">
            Install Hariom Verma Portfolio for a faster and offline-ready experience!
          </p>
          <button
            onClick={handleInstallClick}
            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold py-3 px-6 rounded-lg shadow-lg hover:from-yellow-500 hover:to-yellow-700 transition duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-75"
          >
            Install App
          </button>
        </div>
      )}

      {/* Conditional Instructions for iOS users */}
      {isIOS() && showInstallButton && !window.matchMedia('(display-mode: standalone)').matches && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 p-4 bg-blue-700 text-white rounded-xl shadow-2xl z-50 flex flex-col items-center justify-center space-y-2 max-w-sm w-11/12 md:w-auto animate-fade-in-up">
          <button
            onClick={handleDismissAlert}
            className="absolute top-2 right-2 text-blue-200 hover:text-white text-lg font-bold"
            aria-label="Dismiss"
          >
            &times;
          </button>
          <p className="text-sm text-center">
            For iOS users: Tap the <span className="font-bold">Share</span> button <span className="text-lg">⬆️</span> in your browser's toolbar and then 'Add to Home Screen' to install!
          </p>
        </div>
      )}
    </div>
  );
};

export default App;
